import { useState } from "react";
import { Button } from "@/shared/ui/button";
import { CalendarIcon, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { cn } from "@/shared/lib/utils";

interface MonthPickerProps {
  value?: { month: number; year: number } | null;
  onChange: (value: { month: number; year: number } | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MonthPicker({ value, onChange, placeholder = "Selecione um mês", className, disabled = false }: MonthPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const months = [
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const handleMonthChange = (month: string) => {
    if (value) {
      onChange({ ...value, month: Number(month) });
    } else {
      onChange({ month: Number(month), year: currentYear });
    }
  };

  const handleYearChange = (year: string) => {
    if (value) {
      onChange({ ...value, year: Number(year) });
    } else {
      onChange({ month: 1, year: Number(year) });
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const formatValue = () => {
    if (!value) return placeholder;
    const monthLabel = months.find(m => m.value === value.month)?.label || "";
    return `${monthLabel}/${value.year}`;
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => !disabled && setIsOpen(open)}>
      <PopoverTrigger asChild>
        <div className={cn("flex items-center")}>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn("min-w-[270px] w-full justify-start text-left font-normal bg-[#1A1A1A] text-white/90 border-white/[0.06]", className)}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? (
              <div className="flex items-center gap-1">
                <span>{formatValue()}</span>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="h-7 ml-1 text-xs"
                  >
                    <div className="flex items-end justify-end">
                      <X className="h-4 w-4 text-red-500 " />
                    </div>
                  </Button>
                )}
              </div>
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 bg-[#1A1A1A] text-white/90 border-white/[0.06]">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <Select
              value={value?.month?.toString()}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="w-[140px] bg-[#1A1A1A] text-white/90 border-white/[0.06]">
                <SelectValue placeholder="Mês" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] text-white/90 border-white/[0.06]">
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value.toString()}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={value?.year?.toString()}
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="w-[100px] bg-[#1A1A1A] text-white/90 border-white/[0.06]">
                <SelectValue placeholder="Ano" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] text-white/90 border-white/[0.06]">
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="w-full"
            >
              Limpar
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

